<template>
    <article
        class="singleModeTicket singleTicket betTicket"
        v-bind:class="[
            `_${layout}`,
            {
                _disabled: ticket.isPendingRemoval || !isValid,
            },
        ]"
        v-bind:aria-disabled="ticket.isPendingRemoval || !isValid"
    >
        <div class="betTicket-row">
            <button
                data-testid="remove-ticket"
                type="button"
                class="betTicket-remove"
                v-bind:class="`${gaClassPrefix}_Btn_DelTicket${delTicketGaSuffix}`"
                aria-label="Remove selection"
                v-bind:disabled="isBetSlipInteractionLocked"
                v-on:click="emit('remove')"
            >
                <SboIcon name="system:cross" size="16" />
            </button>
            <div class="betTicket-info">
                <div class="betTicket-league">
                    <span class="betTicket-leagueName">{{ leagueName }}</span>
                </div>
                <div class="betTicket-match">
                    <SportIcon v-bind:sportType="ticket.event.sportType" class="betTicket-sport" />
                    <BetTicketLiveTag v-if="ticket.odds.isLive" />
                    <span class="betTicket-teams">
                        <span v-if="isShowLiveScore" class="notranslate betTicket-liveScore">[{{ displayTicket.odds.eventResult.liveHomeScore }}:{{ displayTicket.odds.eventResult.liveAwayScore }}]</span>
                        {{ homeTeam }} - {{ awayTeam }}
                    </span>
                </div>
                <div class="betTicket-line">
                    <span
                        class="ticketOddsValue betTicket-price"
                        v-bind:class="[oddsChangeClass, { negative: isOddsNegative }]"
                        data-static-odds-change="true"
                    >
                        <span class="betTicket-priceArrowWrap">
                            <SboIcon
                                name="system:arrowDropUp"
                                class="betTicket-priceArrow _rise"
                                size="14"
                            />
                            <SboIcon
                                name="system:arrowDropDown"
                                class="betTicket-priceArrow _drop"
                                size="14"
                            />
                        </span>
                        {{ displayPrice }}
                    </span>
                    <div class="betTicket-lineMain">
                        <span class="betTicket-market">{{ marketName }}</span>
                        <SboIcon
                            v-if="isDisplayCashOutIcon"
                            name="system:cashOut"
                            class="betTicket-cashOut"
                        />
                        <span class="betTicket-divider" />
                        <div class="betTicket-option">
                            <span class="betTicket-optionName">{{ option }}</span>
                            <span v-if="displayPoint" class="notranslate betTicket-point">{{ displayPoint }}</span>
                        </div>
                    </div>
                    <div v-if="isCorrectScoreAos" class="betTicket-lineSub">
                        {{ betConditionText }}
                    </div>
                </div>
            </div>
        </div>
        <slot />
    </article>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, watch } from 'vue';
    import BetTicketLiveTag from '@/components/betSlip/Single/Ticket/BetTicketLiveTag.vue';
    import { useOddsChangeIndicator } from '@/components/betSlip/Single/Ticket/useOddsChangeIndicator';
    import SboIcon from '@/components/common/SboIcon.vue';
    import SportIcon from '@/components/common/SportIcon.vue';
    import type { IBetSlipCashOut } from '@/interface/IBetSlipCashOut';
    import type { IOdds } from '@/interface/IOdds';
    import type { ISingleTicket } from '@/interface/ITicket';
    import { MarketType, OddsOption, PriceStyle, SingleModeTicketLayout } from '@/interface/enum';
    import { useParlayCashOut, useSingleBetCashOut } from '@/libraries/composables/useBetSlipCashOut';
    import type { IParlaySubBetSnapshot } from '@/libraries/composables/useParlayOpenTicket';
    import { usePriceChange } from '@/libraries/composables/usePriceChange';
    import { useTicketDisplay } from '@/libraries/composables/useTicketDisplay';
    import { switchToEuroPrice } from '@/libraries/helpers/oddsHelper';
    import { i18n } from '@/libraries/i18n';
    import { isMainMarket } from '@/libraries/marketGroupHelper';
    import { useOdds } from '@/libraries/oddsApi/composables/useOdds';
    import { useOddsSubscriptionCallback } from '@/libraries/oddsApi/composables/useOddsSubscriptionCallback';
    import { toFixedDecimalPoint } from '@/libraries/utils/text';
    import { useAccountStore } from '@/stores/accountStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';
    import { EnumPresetFilterType } from '@/types';

    const props = withDefaults(defineProps<{
        ticket: ISingleTicket;
        layout: SingleModeTicketLayout;
        isValid?: boolean;
        isMixParlay?: boolean;
        parlayOdds?: IOdds;
        parlaySubBetResult?: IParlaySubBetSnapshot;
        gaClassPrefix?: string;
        delTicketGaSuffix?: string;
    }>(), {
        isValid: true,
        isMixParlay: false,
        parlayOdds: undefined,
        parlaySubBetResult: undefined,
        gaClassPrefix: '',
        delTicketGaSuffix: '',
    });

    const emit = defineEmits<{
        remove: [];
    }>();

    enum TicketPriceOrigin {
        OpenParlay = 'openParlay',
        MixParlayOdds = 'mixParlayOdds',
        Ticket = 'ticket',
    }

    type TicketPriceSource =
        | { origin: TicketPriceOrigin.OpenParlay, result: IParlaySubBetSnapshot }
        | { origin: TicketPriceOrigin.MixParlayOdds, odds: IOdds }
        | { origin: TicketPriceOrigin.Ticket };

    const store = useSingleModeBetSlipStore();
    const {
        isBetSlipInteractionLocked,
        isParlaySubBetCountValid,
        parlaySubBets,
    } = storeToRefs(store);
    const { customerInfo } = storeToRefs(useAccountStore());

    const isOpenParlaySubBet = computed(() => (
        isParlaySubBetCountValid.value
        && parlaySubBets.value.some(ticket => ticket.key === props.ticket.key)
    ));
    const activeParlaySubBetResult = computed(() => (
        props.isMixParlay && props.parlaySubBetResult?.isSuccess
            ? props.parlaySubBetResult
            : null
    ));
    const hasDisplayReadyParlayPrice = computed(() => (
        activeParlaySubBetResult.value !== null
        || props.parlayOdds !== undefined
    ));
    const priceSource = computed<TicketPriceSource>(() => {
        const parlaySubBetResult = activeParlaySubBetResult.value;
        if (parlaySubBetResult !== null) {
            return { origin: TicketPriceOrigin.OpenParlay, result: parlaySubBetResult };
        }
        return props.parlayOdds === undefined
            ? { origin: TicketPriceOrigin.Ticket }
            : { origin: TicketPriceOrigin.MixParlayOdds, odds: props.parlayOdds };
    });

    const displayOdds = computed<IOdds>(() => {
        const source = priceSource.value;
        if (source.origin === TicketPriceOrigin.MixParlayOdds) {
            return source.odds;
        }
        if (source.origin === TicketPriceOrigin.Ticket) {
            return props.ticket.odds;
        }
        return {
            ...props.ticket.odds,
            point: source.result.point,
            betCondition: source.result.betCondition ?? '',
            eventResult: {
                ...props.ticket.odds.eventResult,
                liveHomeScore: source.result.score.home,
                liveAwayScore: source.result.score.away,
            },
        };
    });

    const displayPriceOption = computed(() => {
        const source = priceSource.value;
        const matchedPriceOption = displayOdds.value.priceOption.find(
            priceOption => priceOption.option === props.ticket.option,
        ) ?? {
            option: props.ticket.option,
            oddsOption: OddsOption.fromOddsApi(
                MarketType.toOddsApi(props.ticket.odds.marketType),
                props.ticket.option,
            ),
            optionName: '',
            optionNameShort: '',
            price: props.ticket.price,
        };
        if (source.origin === TicketPriceOrigin.MixParlayOdds) {
            return matchedPriceOption;
        }
        return {
            ...matchedPriceOption,
            price: source.origin === TicketPriceOrigin.OpenParlay
                ? source.result.price
                : props.ticket.price,
        };
    });

    const displayTicket = computed<ISingleTicket>(() => {
        const source = priceSource.value;
        return {
            ...props.ticket,
            odds: displayOdds.value,
            price: displayPriceOption.value.price,
            priceStyle: source.origin === TicketPriceOrigin.OpenParlay
                ? source.result.priceStyle
                : props.ticket.priceStyle,
        };
    });

    const isParlayPricePending = computed(() => (
        props.layout === SingleModeTicketLayout.Parlay
        && props.isMixParlay
        && priceSource.value.origin === TicketPriceOrigin.Ticket
    ));

    const displayPriceValue = computed(() => {
        const { price, priceStyle } = displayTicket.value;
        if (
            props.layout === SingleModeTicketLayout.Parlay
            && !props.isMixParlay
            && priceStyle !== PriceStyle.Unknown
        ) {
            return switchToEuroPrice(price, priceStyle);
        }
        return price;
    });

    const {
        leagueName,
        homeTeam,
        awayTeam,
        marketGroupName,
        option,
        point,
        displayPrice: formattedDisplayPrice,
        isOddsNegative: isFormattedOddsNegative,
        isShowLiveScore,
        isCorrectScoreAos,
    } = useTicketDisplay(displayTicket, { displayPriceValue });
    const displayPrice = computed(() => (
        isParlayPricePending.value ? '--' : formattedDisplayPrice.value
    ));
    const isOddsNegative = computed(() => (
        !isParlayPricePending.value && isFormattedOddsNegative.value
    ));

    const marketName = computed(() => `${marketGroupName.value} ${i18n.t(props.ticket.odds.marketDisplayTypeName)}`.trim());
    const displayPoint = computed(() => toFixedDecimalPoint(point.value, 2));
    const betConditionText = computed(() => i18n.t('odds_option_cs_excluding') + displayTicket.value.odds.betCondition);
    const parlayOddsPrice = computed(() => {
        const price = props.parlayOdds?.priceOption.find(
            priceOption => priceOption.option === props.ticket.option,
        )?.price;
        return price === undefined
            ? null
            : { value: price, priceStyle: PriceStyle.Euro };
    });
    const { priceChangeStatus: parlayOddsPriceChangeStatus } = usePriceChange(parlayOddsPrice);
    const displayedPriceChangeStatus = computed(() => {
        if (!props.isMixParlay) {
            return props.ticket.oddsChangeStatus;
        }
        if (activeParlaySubBetResult.value !== null) {
            return activeParlaySubBetResult.value.priceChangeStatus;
        }
        return parlayOddsPriceChangeStatus.value;
    });
    const { oddsChangeClass } = useOddsChangeIndicator(
        displayedPriceChangeStatus,
        computed(() => displayTicket.value.price),
    );

    const betSlipCashOut = computed<IBetSlipCashOut>(() => ({
        sportType: props.ticket.event.sportType,
        isMainMarket: isMainMarket(props.ticket.odds.marketGroup.id),
        isLive: props.ticket.odds.isLive,
        leagueId: props.ticket.event.leagueId,
        marketType: props.ticket.odds.marketType,
    }));
    const { isDisplayCashOutIcon: isSingleBetCashOutSupported } = useSingleBetCashOut(betSlipCashOut);
    const { isDisplayCashOutIcon: isParlayCashOutSupported } = useParlayCashOut(
        computed(() => [betSlipCashOut.value]),
    );
    const isDisplayCashOutIcon = computed(() => (
        props.layout === SingleModeTicketLayout.Parlay
            ? props.ticket.hasMixParlay && isParlayCashOutSupported.value
            : isSingleBetCashOutSupported.value
    ));

    const shouldUseParlayOdds = computed(() => (
        props.isMixParlay
        && (
            !isOpenParlaySubBet.value
            || !hasDisplayReadyParlayPrice.value
        )
    ));
    const {
        error: parlayOddsError,
        loaded: isParlayOddsLoaded,
        oddsList: parlayOddsList,
    } = useOdds({
        eventId: computed(() => props.ticket.eventId),
        oddsId: computed(() => props.ticket.oddsId),
        isMixParlayMode: true,
        presetFilter: computed(() => (
            props.ticket.odds.isLive
                ? EnumPresetFilterType.Live
                : EnumPresetFilterType.NonLive
        )),
        isLive: computed(() => props.ticket.odds.isLive),
        isEnabled: shouldUseParlayOdds,
        sportType: computed(() => props.ticket.event.sportType),
        leagueSize: computed(() => props.ticket.event.leagueSize),
    });

    watch(
        [isParlayOddsLoaded, parlayOddsList, parlayOddsError],
        ([loaded, oddsList, error]) => {
            if (!shouldUseParlayOdds.value || !loaded || error) {
                return;
            }
            const odds = oddsList.find(item => item.oddsId === props.ticket.oddsId);
            if (odds && store.updateParlayOdds(props.ticket.key, odds)) {
                return;
            }
            store.markTicketClosed(props.ticket.key, 'betSlip_errorMessage_oddsClosed');
        },
        { immediate: true },
    );

    const shouldUseOpenTicketSubscription = computed(() => (
        !props.isMixParlay || isOpenParlaySubBet.value
    ));
    const { onUpdated, onDeleted } = useOddsSubscriptionCallback({
        eventId: props.ticket.eventId,
        oddsId: props.ticket.oddsId,
        isLive: props.ticket.odds.isLive,
        isMixParlay: props.isMixParlay,
        isActive: shouldUseOpenTicketSubscription,
        priceStyle: computed(() => customerInfo.value.priceStyle),
        sportType: props.ticket.event.sportType,
        leagueSize: props.ticket.event.leagueSize,
    });

    onUpdated(() => {
        if (props.isMixParlay) {
            return store.queueParlayOpenTicketRefresh();
        }
        return store.queueTicketRefresh(props.ticket.key);
    });
    onDeleted(() => {
        if (props.isMixParlay) {
            return store.queueParlayOpenTicketRefresh();
        }
        return store.markTicketClosed(props.ticket.key, 'betSlip_errorMessage_oddsClosed');
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/singleTicket";
</style>
